/**
 * Pacote 4, bloco 3 (rev4 aprovada em 17/10/2026; refs. 08/09/11/13) — ficha da Ordem de Locação (OL):
 * cabeçalho compacto; cards (Prazo, Mensalidade, Total do contrato, Margem bruta conforme a regra); área principal
 * (acompanhamento com Emissão → Envio ao Parceiro → Crédito → Contrato → Liberação → Remessa/NF → Implantação,
 * Referências do Cliente, Parceiro/Inttrix, Faturamento/Entrega, Condições negociadas ANTES dos itens, itens) e
 * lateral (detalhes financeiros, remessas, parcelas, regras do parceiro, histórico). Em telas menores a lateral vira
 * uma fileira de recolhíveis acima do acompanhamento.
 */
import { useState, type ReactNode } from 'react';
import { api } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { dataBr } from '../../../backend/src/compartilhado/oportunidades.ts';
import { enderecoTexto, liquidaEstimada } from '../../../backend/src/compartilhado/propostas.ts';
import { textoReajuste } from '../../../backend/src/compartilhado/comercial.ts';
import type { ConteudoPedido } from '../../../backend/src/compartilhado/pedidos.ts';
import { Icone } from '../componentes/icones.tsx';
import { Ajuda, CardLista, CardsLista, EstadoCarga, Modal, SeloSituacao } from './ui.tsx';
import { CabecalhoOrdem, Empilha, FaixaCondicoes, Junta, Par, Passos, QuadroCampos, QuadroLocal, Recolhivel, TextoHistorico, type Etapa } from './FichaOrdem.tsx';
import { ModaisLocacao, ParcelasLocacao, ProximaAcaoLocacao, QuadroRegraCobranca, RegistrosEtapasLocacao, useLocacao, type Acompanhamento, type CtxLocacao, type ReferenciasCliente } from './Locacao.tsx';
import { moeda2, pctNum } from './proposta/formatos.ts';
import { BotaoExcluir, depoisDeExcluir } from './Lixeira.tsx';
import type { DocCompleto, Historico } from './Ordens.tsx';

const quando = (dh: string) => `${dataBr(dh.slice(0, 10))} ${dh.slice(11, 16)}`;
type L = NonNullable<ConteudoPedido['locacao']>;

/** Regra de margem da OL: única (valor total) ou recorrente (por parcela/mensal). */
export function regraDaOl(l: L) {
  const id = l.regraComissao?.regra ?? (l.base === 'parcela' ? 'mensal_projeto' : 'unica_projeto');
  const percentual = Number(l.regraComissao?.percentual ?? l.percentual);
  const unica = id === 'unica_projeto' || id === 'unica_venda';
  const nome = { por_parcela: 'por parcela', mensal_projeto: 'mensal', unica_projeto: 'única, sobre o total do contrato', unica_venda: 'única, sobre o total da venda' }[id];
  return { id, percentual, unica, nome };
}

/** Cards da OL (fonte B; valores na mesma linha): a margem segue a regra — única mostra o valor total. */
export function CardsOl({ l }: { l: L }) {
  const r = regraDaOl(l);
  const mensal = l.periodicidade === 'Mensal';
  return (
    <CardsLista n={4} teste="cards-ol">
      <CardLista icone="relogio" tom="verde" rotulo="Prazo" valor={`${l.prazoMeses} meses`} teste="card-prazo-ol" />
      <CardLista icone="fatura" tom="roxo" rotulo={mensal ? 'Mensalidade' : `Parcela ${l.periodicidade.toLowerCase()}`} valor={moeda2(l.clienteParcela)} teste="card-mensalidade-ol" />
      <CardLista icone="dinheiro" tom="verde" rotulo="Total do contrato" valor={moeda2(l.totalContrato)} teste="card-total-contrato-ol" ajuda={<Ajuda titulo="Total do contrato">Parcela × cobranças, na base contratada (sem projetar reajustes).</Ajuda>} />
      {r.unica ? (
        <CardLista icone="pizza" tom="ambar" rotulo="Margem bruta (única)" valor={moeda2(l.comissaoTotal)} detalhe={`${pctNum(r.percentual)} do total do contrato`} teste="card-margem-ol" ajuda={<Ajuda titulo="Margem bruta (única)">Paga uma única vez, sobre o total do contrato, conforme a regra aceita na Proposta.</Ajuda>} />
      ) : (
        <CardLista icone="pizza" tom="ambar" rotulo={r.id === 'por_parcela' ? 'Margem bruta por parcela' : 'Margem bruta mensal'} valor={moeda2(l.comissaoParcela)} detalhe={pctNum(r.percentual)} teste="card-margem-ol" ajuda={<Ajuda titulo="Margem bruta">Recorrente, conforme a regra aceita na Proposta. O total no contrato está nas Condições negociadas.</Ajuda>} />
      )}
    </CardsLista>
  );
}

/** Condições negociadas (antes dos itens): a regra explica o card; na regra única o valor não se repete. */
export function CondicoesNegociadas({ l, teste }: { l: L; teste?: string }) {
  const r = regraDaOl(l);
  const itens: [string, ReactNode][] = [
    ['Prazo', `${l.prazoMeses} meses`],
    ['Cobrança', `${l.periodicidade} · ${l.cobrancas}×`],
    ['Pagamento', l.formaPagamento || 'não informado'],
    ['Regra de margem', r.unica ? `${pctNum(r.percentual)} sobre o total do contrato, paga uma única vez` : `${pctNum(r.percentual)} ${r.nome}`],
  ];
  if (!r.unica) itens.push(['Margem bruta total no contrato', <strong key="m">{moeda2(l.comissaoTotal)}</strong>]);
  itens.push(['Reajuste', textoReajuste(l.reajuste)]);
  return <FaixaCondicoes titulo="Condições negociadas" itens={itens} teste={teste} />;
}

function etapasOl(doc: DocCompleto, a: Acompanhamento | null): Etapa[] {
  const ativa = doc.situacao !== 'cancelada';
  const d = (id: string) => a?.etapas.find((e) => e.id === id) ?? null;
  const fato = (id: string) => a?.registrosEtapa.filter((r) => r.etapa === id && !r.anulada).map((r) => r.data).sort().at(-1) ?? null;
  const emitida = Boolean(doc.emitida);
  const lista: Omit<Etapa, 'estado'>[] & { feito: boolean }[] = [];
  const add = (nome: string, feito: boolean, sub: ReactNode, teste: string) => lista.push({ nome, feito, sub, teste } as never);
  add('Emissão', emitida, emitida ? `Emitida em ${quando(doc.emitida!.dataHora)}` : ativa ? 'A conferir' : '—', 'etapa-ol-emissao');
  const env = d('po_enviada');
  add('Envio ao Parceiro', Boolean(env?.data), env?.data ? `Enviada em ${dataBr(env.data)}` : 'Pendente', 'etapa-ol-envio');
  const aprov = fato('credito_aprovado');
  const recus = fato('credito_recusado');
  const anal = d('analise_credito');
  add('Crédito', Boolean(aprov), aprov ? `Aprovado em ${dataBr(aprov)}` : recus ? `Recusado em ${dataBr(recus)}` : anal?.data ? `Em análise desde ${dataBr(anal.data)}` : 'Não informado', 'etapa-ol-credito');
  const assin = d('assinatura_conferida');
  const contr = d('contrato_enviado');
  add('Contrato', Boolean(assin?.data), assin?.data ? `Assinatura conferida em ${dataBr(assin.data)}` : contr?.data ? `Enviado em ${dataBr(contr.data)}` : 'Não informado', 'etapa-ol-contrato');
  const lib = d('liberacao');
  add('Liberação', Boolean(lib?.data), lib?.data ? `Liberada em ${dataBr(lib.data)}` : 'Conforme estoque', 'etapa-ol-liberacao');
  const rem = d('remessa');
  add('Remessa / NF', Boolean(rem?.data), rem?.data ? `1ª NF em ${dataBr(rem.data)}` : 'Não informada', 'etapa-ol-remessa');
  const imp = d('implantacao');
  add('Implantação', Boolean(imp?.data), imp?.data ? `Em ${dataBr(imp.data)}` : 'Não informada', 'etapa-ol-implantacao');
  const primeiraPendente = (lista as unknown as { feito: boolean }[]).findIndex((x) => !x.feito);
  return (lista as unknown as (Etapa & { feito: boolean })[]).map((x, i) => ({ nome: x.nome, sub: x.sub, teste: x.teste, estado: x.feito ? 'concluida' : ativa && i === primeiraPendente ? 'atual' : 'pendente' }));
}

/** Faixa das Referências do Cliente: preenchidas, ou o que falta; ações de preencher/editar/consultar. */
function FaixaReferencias({ r, podeEditar, aoEditar, aoConsultar }: { r: ReferenciasCliente; podeEditar: boolean; aoEditar: () => void; aoConsultar: () => void }) {
  return (
    <div className={`faixa-refs${r.preenchidas ? '' : ' pend'}`} data-teste="faixa-referencias">
      <Icone nome="contatos" />
      <strong>Referências do Cliente</strong> ·{' '}
      {r.preenchidas ? (
        <span className="ok">Preenchidas</span>
      ) : (
        <span className="pendente" title={`Falta: ${r.faltas.join(', ')}`}>
          {r.faltas.length} pendência(s)
        </span>
      )}
      <Ajuda titulo="Referências do Cliente">{`Contrato social (anexo separado), procurador de cada unidade, 2 testemunhas, 3 referências comerciais e 1 referência bancária. Vão no PDF da OL. Salvar não marca conferência nem envio.${r.preenchidas ? '' : ` Falta: ${r.faltas.join(', ')}.`}`}</Ajuda>
      <span className="acoes">
        {(r.preenchidas || r.procuradores.length > 0 || r.testemunhas.length > 0) && (
          <button type="button" className="botao-secundario" onClick={aoConsultar} data-teste="consultar-referencias">
            Consultar
          </button>
        )}
        {podeEditar && (
          <button type="button" className="botao-primario" onClick={aoEditar} data-teste="editar-referencias">
            {r.preenchidas || r.procuradores.length || r.testemunhas.length || r.comerciais.length ? 'Editar referências do Cliente' : 'Preencher referências do Cliente'}
          </button>
        )}
      </span>
    </div>
  );
}

const Tab = ({ cab, linhas, teste }: { cab: string[]; linhas: ReactNode[][]; teste?: string }) => (
  <div className="rolagem quadro-tabela">
    <table className="tabela tabela-compacta tabela-refs" data-teste={teste}>
      <thead>
        <tr>
          {cab.map((c) => (
            <th key={c}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {linhas.length ? (
          linhas.map((l, i) => (
            <tr key={i}>
              {l.map((x, k) => (
                <td key={k} className={cab[k] === 'E-mail' ? 'email' : undefined}>
                  {x || '—'}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={cab.length} className="texto-secundario">
              Não informado
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

/** Bloco das Referências do Cliente (consulta). */
export function BlocoReferencias({ r, teste = 'bloco-referencias' }: { r: ReferenciasCliente; teste?: string }) {
  return (
    <section className="refs-bloco" data-teste={teste} id="referencias-cliente">
      <h3>
        <Icone nome="contatos" /> Referências do Cliente
      </h3>
      <div className="refs-grade">
        <div className="ref-card c4">
          <h4>
            <Icone nome="documento" /> Contrato social
          </h4>
          <Tab cab={['Arquivo']} linhas={r.contratoSocial.map((d) => [<span key={d.id}>{d.nome} <span className="selo selo-neutro">anexo separado</span>{d.inativo ? <span className="sec"> (inativo)</span> : null}</span>])} />
        </div>
        <div className="ref-card c8">
          <h4>
            <Icone nome="contatos" /> Procuradores por unidade
          </h4>
          <Tab cab={['Unidade', 'Nome', 'E-mail']} linhas={r.procuradores.map((p) => [p.unidade, p.nome, p.email])} teste="refs-procuradores" />
        </div>
        <div className="ref-card c5">
          <h4>
            <Icone nome="contatos" /> Testemunhas
          </h4>
          <Tab cab={['Nome', 'E-mail']} linhas={r.testemunhas.map((t) => [t.nome, t.email])} teste="refs-testemunhas" />
        </div>
        <div className="ref-card c7">
          <h4>
            <Icone nome="dinheiro" /> Referência bancária
          </h4>
          <Tab cab={['Banco', 'Contato', 'Telefone', 'E-mail']} linhas={r.bancaria ? [[r.bancaria.banco, r.bancaria.contato, r.bancaria.telefone, r.bancaria.email]] : []} teste="refs-bancaria" />
        </div>
        <div className="ref-card c12">
          <h4>
            <Icone nome="predio" /> Referências comerciais
          </h4>
          <Tab cab={['Empresa', 'Telefone', 'E-mail']} linhas={r.comerciais.map((c) => [c.empresa, c.telefone, c.email])} teste="refs-comerciais" />
        </div>
      </div>
    </section>
  );
}

export function FichaOl({ doc, aplicar }: { doc: DocCompleto; aplicar: (x: DocCompleto) => void }) {
  const { pode } = useSessao();
  const ctx = useLocacao(doc.numero);
  const [modal, setModal] = useState<null | 'conferir' | 'referencias' | 'cancelar'>(null);
  const [verParcelas, setVerParcelas] = useState(false);
  const [verRefs, setVerRefs] = useState(false);
  const p = doc.pedidoCompleto;
  const c = p.conteudo;
  const l = c.locacao!;
  const e = doc.entidade;
  const cad = p.cadastro;
  const a = ctx.a;
  const ativa = doc.situacao !== 'cancelada';
  const podeEditar = pode('editar', 'ordem');
  const fat = c.faturamento;
  const ent = c.entrega;
  const mesmoEnd = ent.origem === 'mesmo endereço do faturamento' || enderecoTexto(ent.endereco) === enderecoTexto(fat.endereco);
  const r = regraDaOl(l);
  const abat = c.abatimento && !c.abatimento.naoConfigurado ? c.abatimento : null;
  const remessasValidas = a?.remessas.filter((x) => !x.anulada) ?? [];
  const parcelas = remessasValidas.flatMap((x) => x.parcelas);
  const emConferencia = ativa && doc.situacao === 'em_conferencia';
  const acoes = (
    <>
      {emConferencia && podeEditar && (
        <button type="button" className="botao-primario" onClick={() => setModal('conferir')} data-teste="conferir-emissao-ol">
          Conferir emissão
        </button>
      )}
      {ctx.podeRegistrar && (
        <>
          <button type="button" className={emConferencia ? 'botao-secundario' : 'botao-primario'} onClick={() => ctx.setModal({ tipo: 'etapa' })} data-teste="registrar-etapa">
            Registrar etapa
          </button>
          <button type="button" className="botao-secundario" onClick={() => ctx.setModal({ tipo: 'acompanhamento' })} data-teste="registrar-pendencia">
            Próxima ação
          </button>
        </>
      )}
    </>
  );
  const lateral = (
    <aside className="lat" aria-label="Detalhes da OL">
      <Recolhivel titulo="Detalhes financeiros" icone="dinheiro" teste="ol-detalhes-financeiros">
        <dl className="pares-inline">
          <div>
            <dt>Total do contrato</dt>
            <dd>{moeda2(l.totalContrato)}</dd>
          </div>
          <div>
            <dt>Margem bruta total</dt>
            <dd>
              {moeda2(l.comissaoTotal)} ({pctNum(r.percentual)})
            </dd>
          </div>
          <div>
            <dt>
              Líquido do Parceiro <span className="sec">(interno)</span>
            </dt>
            <dd>{moeda2(l.liquidoTotal)}</dd>
          </div>
          <div>
            <dt>
              Comissão líquida estimada <span className="sec">(interna)</span>
            </dt>
            <dd>{abat ? moeda2(liquidaEstimada(l.comissaoTotal, abat.percentual)) : 'abatimento não configurado na versão'}</dd>
          </div>
        </dl>
        <p className="nota-discreta">Nada disto vai ao PDF do Parceiro.</p>
      </Recolhivel>
      <Recolhivel titulo="Remessas" icone="caminhao" aberto teste="ol-remessas" extra={remessasValidas.length ? <span className="contador">{remessasValidas.length}</span> : undefined}>
        {remessasValidas.length ? (
          <ul className="lista-simples">
            {remessasValidas.map((x) => (
              <li key={x.id}>
                NF {x.nf} · {dataBr(x.dataRemessa)} · {x.quantidade} un.
              </li>
            ))}
          </ul>
        ) : (
          <p className="info-lat">{a && a.etapas.find((x) => x.id === 'liberacao')?.data ? 'Nenhuma remessa registrada.' : 'Disponível após a liberação da remessa.'}</p>
        )}
        {ctx.podeRegistrar && (
          <button type="button" className="botao-secundario pequeno" onClick={() => ctx.setModal({ tipo: 'remessa' })} data-teste="registrar-remessa">
            Registrar remessa (NF)
          </button>
        )}
      </Recolhivel>
      <Recolhivel titulo="Parcelas de faturamento" icone="fatura" aberto teste="ol-parcelas">
        {parcelas.length ? (
          <p className="info-lat">
            {parcelas.length} parcela(s) · {parcelas.filter((x) => x.situacao === 'Paga e recebida').length} paga(s) e recebida(s)
          </p>
        ) : (
          <p className="info-lat">Aguardando a primeira cobrança.</p>
        )}
        {(a?.remessas.length ?? 0) > 0 && (
          <button
            type="button"
            className="botao-secundario pequeno"
            onClick={() => {
              setVerParcelas(true);
              setTimeout(() => document.getElementById('parcelas-locacao')?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 50);
            }}
            data-teste="ver-parcelas"
          >
            Ver parcelas e registros
          </button>
        )}
      </Recolhivel>
      <Recolhivel titulo="Regras do parceiro" icone="parametros" teste="ol-regras">
        {a && <QuadroRegraCobranca regra={a.regra} titulo={`Regra de cobrança · ${a.locadora?.nome ?? 'parceiro'}`} origem={a.origemRegra} />}
        <dl className="pares-inline">
          {(
            [
              ['Renovação', l.condicoes?.renovacao],
              ['Cancelamento', l.condicoes?.cancelamento],
              ['Manutenção', l.condicoes?.manutencao],
              ['Substituição', l.condicoes?.substituicao],
              ['Seguro', l.condicoes?.seguro],
              ['Responsabilidades', l.condicoes?.responsabilidades],
            ] as [string, string | undefined][]
          ).map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd className="texto-normal">{v || <span className="ausente">não informado</span>}</dd>
            </div>
          ))}
        </dl>
      </Recolhivel>
      <Recolhivel titulo="Histórico" icone="relogio" teste="ol-historico">
        <RegistrosEtapasLocacao ctx={ctx} />
        <ul className="linha-tempo" data-teste="historico-ordem">
          {doc.historico.map((h: Historico, i) => (
            <li key={i}>
              <strong>{quando(h.dataHora)}</strong> · {h.texto} <span className="texto-secundario">({h.autor})</span>
            </li>
          ))}
          {doc.historico.length === 0 && <li className="texto-secundario">Sem registros.</li>}
        </ul>
      </Recolhivel>
    </aside>
  );
  return (
    <>
      <CabecalhoOrdem
        numero={doc.numero}
        tipo="Ordem de Locação"
        teste="cabecalho-documento"
        status={<SeloSituacao texto={doc.situacaoTexto} tom={doc.situacao === 'emitida' ? 'positivo' : doc.situacao === 'cancelada' ? 'encerrado' : 'backlog'} />}
        acoes={
          <>
            <a className="botao-primario" href={`#/ordens/${doc.numero}/pdf`} data-teste="pdf-documento">
              <Icone nome="documento" /> Visualizar PDF
            </a>
            {ativa && pode('cancelar', 'ordem') && (
              <button type="button" className="botao-secundario" onClick={() => setModal('cancelar')} data-teste="cancelar-documento">
                Cancelar 🔒
              </button>
            )}
            <BotaoExcluir entidade="documento_operacional" id={doc.id} aoExcluir={depoisDeExcluir(`/pedidos/${p.numero}`)} />
          </>
        }
        contexto={[
          <>
            Pedido: <a href={`#/pedidos/${p.numero}`}>{p.numero}</a>
          </>,
          <>
            Cliente final:{' '}
            <a href={`#/clientes/${p.cliente.id}`}>
              {p.cliente.nome} ({fat.cnpj})
            </a>
          </>,
          <a href={`#/oportunidades/${p.oportunidade.id}/propostas/${p.proposta.id}/v/${p.versao}`}>
            Proposta {p.proposta.numero} v{p.versao}
          </a>,
        ]}
      />
      {doc.cancelamento && (
        <div className="aviso aviso-cancelada" data-teste="cancelamento">
          <strong>Cancelada — estado definitivo.</strong> Motivo: {doc.cancelamento.motivo} · por {doc.cancelamento.autor} em {quando(doc.cancelamento.dataHora)}
        </div>
      )}
      <CardsOl l={l} />
      <div className="ficha-2col" data-teste="ficha-ol">
        <div className="main">
          <Passos titulo="Acompanhamento da OL" etapas={etapasOl(doc, a)} acao={acoes} teste="acompanhamento-locacao" />
          <ProximaAcaoLocacao ctx={ctx} />
          {a ? <FaixaReferencias r={a.referencias} podeEditar={ctx.podeRegistrar} aoEditar={() => setModal('referencias')} aoConsultar={() => (setVerRefs(true), setTimeout(() => document.getElementById('referencias-cliente')?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 50))} /> : <EstadoCarga carregando={ctx.carga.carregando} erro={ctx.carga.erro} recarregar={ctx.carga.recarregar} teste="acompanhamento-locacao-carga" />}
          <Par>
            <QuadroCampos
              titulo="Parceiro"
              icone="predio"
              teste="ol-parceiro"
              campos={[
                ['Locadora', e ? e.razao : '—'],
                ['CNPJ', e?.cnpj ?? ''],
                ['Contato', e && (e.contato || e.email || e.telefone) ? <Empilha itens={[e.contato, e.email && <a href={`mailto:${e.email}`}>{e.email}</a>, e.telefone].filter(Boolean) as ReactNode[]} /> : ''],
              ]}
            />
            <QuadroCampos
              titulo="Inttrix"
              icone="predio"
              tom="neutro"
              campos={[
                ['Razão social', c.empresa?.razao ?? ''],
                ['CNPJ', c.empresa?.cnpj ?? ''],
                ['Responsável', <Empilha itens={[p.responsavel.nome, p.responsavel.email && <a href={`mailto:${p.responsavel.email}`}>{p.responsavel.email}</a>].filter(Boolean) as ReactNode[]} />],
              ]}
            />
          </Par>
          <Par>
            <QuadroLocal
              titulo="Faturamento ao cliente final"
              icone="predio"
              tom="turquesa"
              teste="ol-faturamento"
              razao={fat.razao}
              linhas={[
                <Junta partes={[`CNPJ ${fat.cnpj}`, <TextoHistorico rotulo="IE" v={cad.faturamento.ie} dataReferencia={cad.dataReferencia} teste="ie-faturamento" />]} />,
                enderecoTexto(fat.endereco),
                <Junta partes={[fat.contato && `Contato: ${fat.contato}`, <TextoHistorico rotulo="E-mail" v={cad.faturamento.email} dataReferencia={cad.dataReferencia} />, fat.telefone]} />,
                <>
                  OC do Cliente: {p.ocsVigentes.length ? p.ocsVigentes.map((x) => x.numero).join(' · ') : <a href={`#/pedidos/${p.numero}`}>Não informada</a>}
                </>,
              ]}
            />
            <QuadroLocal
              titulo="Entrega e instalação"
              icone="caminhao"
              tom="azul"
              teste="ol-entrega"
              marca={mesmoEnd ? 'Mesmo endereço do faturamento' : undefined}
              razao={ent.razao}
              linhas={[
                <Junta partes={[`CNPJ ${ent.cnpj}`, <TextoHistorico rotulo="IE" v={cad.entrega.ie} dataReferencia={cad.dataReferencia} />]} />,
                enderecoTexto(ent.endereco),
                <Junta partes={[ent.contato && `Contato: ${ent.contato}`, ent.telefone]} />,
                <span data-teste="doc-logistica">
                  <Junta partes={[`Implantação: ${l.dataImplantacao ? dataBr(l.dataImplantacao) : l.implantacao || 'a definir'}`, `Responsável pela entrega: ${l.responsavelEntrega || '—'}`, `Responsável pela implantação: ${l.responsavelImplantacao || '—'}`]} />
                </span>,
              ]}
            />
          </Par>
          <CondicoesNegociadas l={l} teste="ol-condicoes" />
          <h2 className="titulo-tab">Itens da Ordem de Locação</h2>
          <div className="rolagem quadro-tabela">
            <table className="tabela tabela-compacta" data-teste="componentes-documento">
              <thead>
                <tr>
                  <th>PN</th>
                  <th>Descrição</th>
                  <th className="numero">Qtd</th>
                </tr>
              </thead>
              <tbody>
                {l.componentes.map((x) => (
                  <tr key={x.id}>
                    <td className="nowrap">{x.pn || '—'}</td>
                    <td>{x.descricao}</td>
                    <td className="numero">{x.quantidadeAceita}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {l.observacoes && <p className="texto-secundario">Observações: {l.observacoes}</p>}
        </div>
        {lateral}
      </div>
      {a && (verRefs || a.referencias.preenchidas) && <BlocoReferencias r={a.referencias} />}
      {(verParcelas || ctx.modal?.tipo === 'datas') && a && (
        <section className="secao-parcelas-ol" id="parcelas-locacao" data-teste="secao-parcelas">
          <div className="cab-secao">
            <h2 className="titulo-tab">Remessas e parcelas</h2>
            <button type="button" className="botao-link" onClick={() => setVerParcelas(false)}>
              Recolher
            </button>
          </div>
          <ParcelasLocacao ctx={ctx} />
        </section>
      )}
      <ModaisLocacao ctx={ctx} />
      {modal === 'conferir' && a && <ModalConferirEmissaoOl doc={doc} a={a} aoFechar={() => setModal(null)} aoSalvar={(x) => (setModal(null), aplicar(x), void ctx.carga.recarregar())} />}
      {modal === 'referencias' && a && <ModalReferencias ctx={ctx} r={a.referencias} aoFechar={() => setModal(null)} />}
      {modal === 'cancelar' && <ModalCancelarOl doc={doc} aoFechar={() => setModal(null)} aoSalvar={(x) => (setModal(null), aplicar(x))} />}
    </>
  );
}

function ModalCancelarOl({ doc, aoFechar, aoSalvar }: { doc: DocCompleto; aoFechar: () => void; aoSalvar: (x: DocCompleto) => void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  return (
    <Modal
      titulo={`Cancelar ${doc.numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={!motivo.trim()}
            onClick={() =>
              void api
                .post<DocCompleto>(`/api/ordens/${doc.numero}/cancelar`, { motivo: motivo.trim() })
                .then(aoSalvar)
                .catch((x: Error) => setErro(x.message))
            }
            data-teste="confirmar-cancelar-documento"
          >
            Confirmar cancelamento
          </button>
        </>
      }
    >
      {erro && <p className="aviso aviso-erro">{erro}</p>}
      <p className="aviso aviso-info">Ação sensível: o cancelamento é definitivo, com motivo, usuário e data na auditoria. O Pedido não muda.</p>
      <label className="campo">
        Motivo (obrigatório)
        <input value={motivo} onChange={(x) => setMotivo(x.target.value)} maxLength={500} data-teste="motivo-cancelar-documento" />
      </label>
    </Modal>
  );
}

/** I14 — Conferir emissão da OL (refs. 09 e 13): conteúdo à esquerda, painel com destinatário e arquivos à direita. */
function ModalConferirEmissaoOl({ doc, a, aoFechar, aoSalvar }: { doc: DocCompleto; a: Acompanhamento; aoFechar: () => void; aoSalvar: (x: DocCompleto) => void }) {
  const [conferi, setConferi] = useState(false);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const p = doc.pedidoCompleto;
  const c = p.conteudo;
  const l = c.locacao!;
  const cad = p.cadastro;
  const r = a.referencias;
  const e = doc.entidade;
  const emitir = async () => {
    if (enviando) return;
    setEnviando(true);
    setErro('');
    try {
      aoSalvar(await api.post<DocCompleto>(`/api/ordens/${doc.numero}/emitir`, { conferido: true }));
    } catch (x) {
      setErro((x as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo="Conferir emissão da OL"
      largo
      aoFechar={aoFechar}
      rodape={
        <>
          <label className="conferi-ordem">
            <input type="checkbox" checked={conferi} onChange={(x) => setConferi(x.target.checked)} data-teste="conferi-emissao-ol" /> Conferi os dados e os anexos para o Parceiro
            <Ajuda titulo="O que a emissão faz">Ao confirmar, a OL passa a Emitida e o histórico registra quem emitiu. Nada é enviado: o envio ao Parceiro é a etapa “Envio ao Parceiro”, registrada por você com a data, a observação e a evidência. Não há e-mail automático.</Ajuda>
          </label>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar sem emitir
          </button>
          <button type="button" className="botao-primario" disabled={!conferi || enviando} onClick={() => void emitir()} data-teste="emitir-documento">
            Confirmar emissão
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <div className="conferencia-ol">
        <div className="conteudo-conferencia">
          <div className="ctx-modal">
            <div>
              <h3>{doc.numero} · Ordem de Locação</h3>
              <p>
                Pedido: {p.numero} · Cliente final: {p.cliente.nome} ({c.faturamento.cnpj}) · Proposta {p.proposta.numero} v{p.versao}
              </p>
            </div>
            <SeloSituacao texto="Aguardando emissão" tom="backlog" />
          </div>
          <CardsOl l={l} />
          <CondicoesNegociadas l={l} />
          <Par>
            <QuadroCampos titulo="Parceiro" icone="predio" campos={[['Locadora', e?.razao ?? '—'], ['CNPJ', e?.cnpj ?? ''], ['Contato', e && (e.contato || e.email || e.telefone) ? <Empilha itens={[e.contato, e.email, e.telefone].filter(Boolean) as ReactNode[]} /> : '']]} />
            <QuadroCampos titulo="Inttrix" icone="predio" tom="neutro" campos={[['Razão social', c.empresa?.razao ?? ''], ['CNPJ', c.empresa?.cnpj ?? ''], ['Responsável', <Empilha itens={[p.responsavel.nome, p.responsavel.email]} />]]} />
          </Par>
          <Par>
            <QuadroLocal titulo="Faturamento" icone="predio" tom="turquesa" razao={c.faturamento.razao} linhas={[<Junta partes={[`CNPJ ${c.faturamento.cnpj}`, <TextoHistorico rotulo="IE" v={cad.faturamento.ie} dataReferencia={cad.dataReferencia} />]} />, enderecoTexto(c.faturamento.endereco), <Junta partes={[c.faturamento.contato && `Contato: ${c.faturamento.contato}`, <TextoHistorico rotulo="E-mail" v={cad.faturamento.email} dataReferencia={cad.dataReferencia} />]} />, `OC do Cliente: ${p.ocsVigentes.map((x) => x.numero).join(' · ') || 'Não informada'}`]} />
            <QuadroLocal titulo="Entrega" icone="caminhao" tom="azul" razao={c.entrega.razao} linhas={[<Junta partes={[`CNPJ ${c.entrega.cnpj}`, <TextoHistorico rotulo="IE" v={cad.entrega.ie} dataReferencia={cad.dataReferencia} />]} />, enderecoTexto(c.entrega.endereco), <Junta partes={[c.entrega.contato && `Contato: ${c.entrega.contato}`, c.entrega.telefone]} />]} />
          </Par>
          <h2 className="titulo-tab">Itens da Ordem de Locação</h2>
          <Tab cab={['PN', 'Descrição', 'Qtd']} linhas={l.componentes.map((x) => [x.pn || '—', x.descricao, String(x.quantidadeAceita)])} />
          <BlocoReferencias r={r} teste="conferencia-referencias" />
          <h2 className="titulo-tab">Documentos anexos</h2>
          <Tab cab={['#', 'Documento', 'Arquivo', 'Observação']} linhas={r.contratoSocial.map((d, i) => [String(i + 1), 'Contrato social', d.nome, 'Arquivo separado'])} teste="conferencia-anexos" />
        </div>
        <aside className="painel-conferencia">
          <div>
            <h4>Destinatário</h4>
            <div className="caixa">Parceiro: {e?.razao ?? '—'}</div>
          </div>
          <div>
            <h4>Arquivos que serão incluídos</h4>
            <div className="caixa">
              <div className="arq">
                <Icone nome="documento" /> {doc.numero}.pdf
              </div>
              {r.contratoSocial.map((d) => (
                <div className="arq" key={d.id}>
                  <Icone nome="documento" /> {d.nome} <span className="sec">(anexo separado)</span>
                </div>
              ))}
            </div>
          </div>
          {!r.preenchidas && (
            <p className="aviso aviso-alerta" data-teste="faltas-referencias">
              Referências do Cliente incompletas: falta {r.faltas.join(', ')}.
            </p>
          )}
          <p className="nota-discreta">Não será enviado e-mail automático nesta etapa.</p>
        </aside>
      </div>
    </Modal>
  );
}

/** I15 — formulário Referências do Cliente (ref. 10): salvar não marca conferência nem envio. */
function ModalReferencias({ ctx, r, aoFechar }: { ctx: CtxLocacao; r: ReferenciasCliente; aoFechar: () => void }) {
  const [contrato, setContrato] = useState<string[]>(r.contratoSocial.map((d) => d.id));
  const [proc, setProc] = useState(r.procuradores.length ? r.procuradores : [{ entidadeId: null, unidade: '', nome: '', email: '' }]);
  const [test, setTest] = useState(r.testemunhas.length ? r.testemunhas : [{ nome: '', email: '' }, { nome: '', email: '' }]);
  const [com, setCom] = useState(r.comerciais.length ? r.comerciais : [{ empresa: '', telefone: '', email: '' }, { empresa: '', telefone: '', email: '' }, { empresa: '', telefone: '', email: '' }]);
  const [banco, setBanco] = useState(r.bancaria ?? { banco: '', contato: '', telefone: '', email: '' });
  const op = r.opcoes;
  const vazio = (o: Record<string, unknown>) => Object.entries(o).every(([k, v]) => k === 'entidadeId' || !String(v ?? '').trim());
  const salvar = () =>
    void ctx.acao.executar(
      () =>
        api.put<Acompanhamento>(`${ctx.base}/referencias`, {
          contrato_social: contrato,
          procuradores: proc.filter((x) => !vazio(x)),
          testemunhas: test.filter((x) => !vazio(x)),
          comerciais: com.filter((x) => !vazio(x)),
          bancaria: vazio(banco) ? null : banco,
        }),
      aoFechar,
    );
  const reaproveitar = (k: { nome: string; email: string }, f: (x: { nome: string; email: string }) => void) => f(k);
  const SelContato = ({ aoEscolher, teste }: { aoEscolher: (k: { nome: string; email: string }) => void; teste: string }) =>
    op.contatos.length ? (
      <select value="" onChange={(x) => x.target.value !== '' && reaproveitar(op.contatos[Number(x.target.value)], aoEscolher)} aria-label="Reaproveitar contato do Cliente" className="reaproveitar" data-teste={teste}>
        <option value="">Reaproveitar contato…</option>
        {op.contatos.map((k, i) => (
          <option key={k.email} value={i}>
            {k.nome} · {k.email}
          </option>
        ))}
      </select>
    ) : null;
  const Remover = ({ aoRemover, rotulo }: { aoRemover: () => void; rotulo: string }) => (
    <button type="button" className="icone-acao" title={`Remover ${rotulo}`} aria-label={`Remover ${rotulo}`} onClick={aoRemover}>
      ✕
    </button>
  );
  return (
    <Modal
      titulo="Referências do Cliente"
      largo
      aoFechar={aoFechar}
      rodape={
        <>
          <span className="texto-secundario rodape-nota">Dados no PDF da OL · contrato social como anexo separado</span>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar sem salvar
          </button>
          <button type="button" className="botao-primario" disabled={ctx.acao.ocupado} onClick={salvar} data-teste="salvar-referencias">
            Salvar referências do Cliente
          </button>
        </>
      }
    >
      {ctx.acao.erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-referencias">
          {ctx.acao.erro}
        </p>
      )}
      <div className="form3">
        <section className="sec3">
          <div className="cab3">
            <span className="n3">1</span>
            <h3>Contrato social</h3>
          </div>
          <div className="lista-check">
            {op.documentos.length === 0 && <p className="texto-secundario sem-margem">Nenhum documento ativo na Oportunidade. Anexe o contrato social na aba Documentos da Oportunidade.</p>}
            {op.documentos.map((d) => (
              <label key={d.id} className="opcao">
                <input type="checkbox" checked={contrato.includes(d.id)} onChange={(x) => setContrato(x.target.checked ? [...contrato, d.id] : contrato.filter((y) => y !== d.id))} data-teste={`contrato-${d.id}`} /> {d.nome} {d.restrito && <span className="selo selo-neutro">restrito</span>}
              </label>
            ))}
          </div>
        </section>
        <section className="sec3">
          <div className="cab3">
            <span className="n3">2</span>
            <h3>Procuradores por unidade</h3>
            <span className="acoes">
              <button type="button" className="botao-secundario pequeno" onClick={() => setProc([...proc, { entidadeId: null, unidade: '', nome: '', email: '' }])}>
                + Adicionar unidade
              </button>
            </span>
          </div>
          {proc.map((x, i) => (
            <div className="linha-ref" key={i}>
              <select
                value={x.entidadeId ?? ''}
                onChange={(y) => {
                  const u = op.unidades.find((z) => z.id === y.target.value);
                  setProc(proc.map((z, k) => (k === i ? { ...z, entidadeId: u?.id ?? null, unidade: u?.rotulo ?? z.unidade } : z)));
                }}
                aria-label={`Unidade do procurador ${i + 1}`}
                data-teste={`proc-unidade-${i}`}
              >
                <option value="">Unidade…</option>
                {op.unidades.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.rotulo}
                  </option>
                ))}
              </select>
              <input value={x.nome} placeholder="Nome" aria-label={`Nome do procurador ${i + 1}`} onChange={(y) => setProc(proc.map((z, k) => (k === i ? { ...z, nome: y.target.value } : z)))} data-teste={`proc-nome-${i}`} />
              <input value={x.email} placeholder="E-mail" type="email" aria-label={`E-mail do procurador ${i + 1}`} onChange={(y) => setProc(proc.map((z, k) => (k === i ? { ...z, email: y.target.value } : z)))} data-teste={`proc-email-${i}`} />
              <SelContato aoEscolher={(k) => setProc(proc.map((z, j) => (j === i ? { ...z, nome: k.nome, email: k.email } : z)))} teste={`proc-reaproveitar-${i}`} />
              <Remover aoRemover={() => setProc(proc.filter((_, k) => k !== i))} rotulo={`o procurador ${i + 1}`} />
            </div>
          ))}
        </section>
        <section className="sec3">
          <div className="cab3">
            <span className="n3">3</span>
            <h3>Testemunhas (2)</h3>
          </div>
          {test.map((x, i) => (
            <div className="linha-ref" key={i}>
              <input value={x.nome} placeholder="Nome" aria-label={`Nome da testemunha ${i + 1}`} onChange={(y) => setTest(test.map((z, k) => (k === i ? { ...z, nome: y.target.value } : z)))} data-teste={`test-nome-${i}`} />
              <input value={x.email} placeholder="E-mail" type="email" aria-label={`E-mail da testemunha ${i + 1}`} onChange={(y) => setTest(test.map((z, k) => (k === i ? { ...z, email: y.target.value } : z)))} data-teste={`test-email-${i}`} />
              <SelContato aoEscolher={(k) => setTest(test.map((z, j) => (j === i ? k : z)))} teste={`test-reaproveitar-${i}`} />
            </div>
          ))}
        </section>
        <section className="sec3">
          <div className="cab3">
            <span className="n3">4</span>
            <h3>Referências comerciais (3)</h3>
            <Ajuda titulo="Referências comerciais">Informe pelo menos um contato por referência: telefone e/ou e-mail.</Ajuda>
          </div>
          {com.map((x, i) => (
            <div className="linha-ref" key={i}>
              <input value={x.empresa} placeholder="Empresa" aria-label={`Empresa da referência ${i + 1}`} onChange={(y) => setCom(com.map((z, k) => (k === i ? { ...z, empresa: y.target.value } : z)))} data-teste={`com-empresa-${i}`} />
              <input value={x.telefone} placeholder="Telefone" aria-label={`Telefone da referência ${i + 1}`} onChange={(y) => setCom(com.map((z, k) => (k === i ? { ...z, telefone: y.target.value } : z)))} data-teste={`com-telefone-${i}`} />
              <input value={x.email} placeholder="E-mail" type="email" aria-label={`E-mail da referência ${i + 1}`} onChange={(y) => setCom(com.map((z, k) => (k === i ? { ...z, email: y.target.value } : z)))} data-teste={`com-email-${i}`} />
            </div>
          ))}
        </section>
        <section className="sec3">
          <div className="cab3">
            <span className="n3">5</span>
            <h3>Referência bancária (1)</h3>
          </div>
          <div className="linha-ref">
            <input value={banco.banco} placeholder="Banco" aria-label="Banco" onChange={(y) => setBanco({ ...banco, banco: y.target.value })} data-teste="banco-banco" />
            <input value={banco.contato} placeholder="Contato" aria-label="Contato no banco" onChange={(y) => setBanco({ ...banco, contato: y.target.value })} data-teste="banco-contato" />
            <input value={banco.telefone} placeholder="Telefone" aria-label="Telefone do banco" onChange={(y) => setBanco({ ...banco, telefone: y.target.value })} data-teste="banco-telefone" />
            <input value={banco.email} placeholder="E-mail" type="email" aria-label="E-mail do banco" onChange={(y) => setBanco({ ...banco, email: y.target.value })} data-teste="banco-email" />
          </div>
        </section>
      </div>
    </Modal>
  );
}

